import { useState, useEffect, useRef, useTransition, Fragment, useMemo, useCallback } from "react";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ScrollIcon, ExternalLink, BookUser, List, Users, Search, X, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { LoadingState, ErrorState } from "@/components/ui/data-display";
import { useAllUnarchivedPoems, usePoet } from "@/hooks/data";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { Database } from '@/types/supabase';
import { ServiceResult } from '@/utils/service/serviceUtils';
import { AppError, ErrorType } from '@/utils/error/errorHandler';
import { PoemHighlight } from "@/types/highlights";
import { FeaturedPoem, FeaturedPoemHighlight } from "@/data/featured-poems/types";
import HighlightedLine from "@/components/highlight/HighlightedLine";
import HighlightLegend from "@/components/highlight/HighlightLegend";
import type { Json } from '@/types/supabase';
import { assignDistinctColors, hasDistinctColors } from '@/utils/highlightColorAssigner';
import { TooltipProvider as HighlightTooltipProvider } from '@/contexts/TooltipContext';
import { generateFallbackLabel } from '@/utils/highlightLabelUtils';
import { highlightProcessor, ProcessedHighlightData } from '@/services/highlightProcessingService';
import { getPoemById, type PoemWithHighlights } from "@/services/poemService";

// Define types based on Supabase schema
type Poem = Database['public']['Views']['poem_details']['Row'];
type Poet = Database['public']['Tables']['poets']['Row'];

// Helper type for style points
type StylePoint = string;

// Cookie helper functions
const setCookie = (name: string, value: string, days = 365) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + value + expires + "; path=/; SameSite=Lax";
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const Book = () => {
  // IMPORTANT: This component only displays non-archived poems.
  // The poems array from usePoems is already filtered to exclude archived poems.
  // All navigation, selection, and filtering logic works only with non-archived poems.
  // If a user attempts to access an archived poem directly via URL, they will be redirected
  // to an available non-archived poem with appropriate messaging.
  
  const [searchParams] = useSearchParams();
  const poetParam = searchParams.get('poet');
  const poemIdParam = searchParams.get('poemId');
  const subjectParam = searchParams.get('subject');
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Add React useTransition to prioritize UI responsiveness
  const [isPending, startTransition] = useTransition();
  
  // Read the initial showHighlights state from cookie, default to false if not found
  const initialShowHighlights = getCookie('showHighlights') === 'true';
  const [showHighlights, setShowHighlightsState] = useState(initialShowHighlights);
  
  // Custom setShowHighlights function that updates both state and cookie
  const setShowHighlights = (value: boolean) => {
    setShowHighlightsState(value);
    setCookie('showHighlights', value.toString());
  };
  
  // Fetch all un-archived poems (aggregates every page behind the scenes)
  const {
    data: poems,
    isLoading: isPoemsLoading,
    error: poemsError,
    refetch: refetchPoems,
  } = useAllUnarchivedPoems({
    useCache: true,
    cacheTTL: 10 * 60 * 1000,
  });

  // Add state to prevent premature data processing
  const [initialized, setInitialized] = useState(false);
  
  // Keep track of poet ID for poet info fetching
  const [currentPoetId, setCurrentPoetId] = useState<string | null>(null);
  
  // State for current selections
  const [currentPoemIndex, setCurrentPoemIndex] = useState(0);
  const [currentPoem, setCurrentPoem] = useState<Poem | null>(null);
  const [poemsByCurrentPoet, setPoemsByCurrentPoet] = useState<Poem[]>([]);
  const [currentPoetPoemIndex, setCurrentPoetPoemIndex] = useState(0);
  const [uniquePoets, setUniquePoets] = useState<string[]>([]);
  const [currentPoetIndex, setCurrentPoetIndex] = useState(0);
  const [currentPoet, setCurrentPoet] = useState<string>('');
  
  // Add state for poets filtered by the current subject
  const [subjectFilteredPoets, setSubjectFilteredPoets] = useState<string[]>([]);
  
  // State for subjects/topics
  const [uniqueSubjects, setUniqueSubjects] = useState<string[]>([]);
  const [currentSubject, setCurrentSubject] = useState<string>('');
  const [showSubjectList, setShowSubjectList] = useState(false);
  const [poemsByCurrentSubject, setPoemsByCurrentSubject] = useState<Poem[]>([]);
  const [currentSubjectPoemIndex, setCurrentSubjectPoemIndex] = useState(0);
  
  // Add state to track if we're in a transition
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Fetch current poet info using our custom hook with separate dependency tracking
  const {
    data: currentPoetInfo,
    isLoading: isPoetLoading,
    error: poetError,
    refetch: refetchPoet
  } = usePoet({
    poetId: currentPoetId || '',
    dependencies: [currentPoetId],
    fetchOnMount: !!currentPoetId,
    useCache: true,
    cacheTTL: 15 * 60 * 1000
  }) as {
    data: ServiceResult<Poet> | null;
    isLoading: boolean;
    error: AppError | null;
    refetch: () => Promise<void>;
  };
  
  // Animation states
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right' | 'up' | 'down' | null>(null);
  const [showPoetList, setShowPoetList] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  // Refs for animation
  const poemContentRef = useRef<HTMLDivElement>(null);
  const poetNameRef = useRef<HTMLDivElement>(null);

  // Add new state for filtering poets
  const [poetSearchQuery, setPoetSearchQuery] = useState<string>('');
  const [filteredPoets, setFilteredPoets] = useState<string[]>([]);
  
  // Refs for handling click outside
  const poetListRef = useRef<HTMLDivElement>(null);
  const subjectListRef = useRef<HTMLDivElement>(null);
  const poetListButtonRef = useRef<HTMLButtonElement>(null);
  const subjectListButtonRef = useRef<HTMLButtonElement>(null);

  // Add state for active highlight
  const [activeHighlight, setActiveHighlightState] = useState<string | null>(null);

  // Memoize setActiveHighlight to prevent unnecessary re-renders of HighlightedLine
  const setActiveHighlight = useCallback((highlightId: string | null) => {
    setActiveHighlightState(highlightId);
  }, []);

  // Add state for navigation boundaries
  const [isAtStart, setIsAtStart] = useState(false);
  const [isAtEnd, setIsAtEnd] = useState(false);

  // Add state for full poem details
  const [currentPoemDetails, setCurrentPoemDetails] = useState<PoemWithHighlights | null>(null);
  const [isLoadingPoemDetails, setIsLoadingPoemDetails] = useState(false);
  const [poemDetailsError, setPoemDetailsError] = useState<AppError | null>(null);

  // MEMOIZE HIGHLIGHT PROCESSING
  // Calculate processedData only when content/highlights change, or when toggling highlights
  const processedData: ProcessedHighlightData | null = useMemo(() => {
    const poemContent = currentPoemDetails?.content;
    const highlights = currentPoemDetails?.highlights || [];
    const contentLines = typeof poemContent === 'string' 
      ? poemContent.split('\n') 
      : [];

    if (contentLines.length > 0 && showHighlights && highlights.length > 0) {
      // Logging (keep for debugging)
      // console.log(`[Book.tsx useMemo] Calculating processed highlight data...`);
      return highlightProcessor.processHighlightsForDisplay(
        contentLines,
        highlights,
        // Pass undefined here, active state is handled downstream in HighlightedLine
        undefined 
      );
    }
    return null; // Return null if conditions aren't met
    // Dependencies: recalculate only if poem content, highlights, or the toggle changes
  }, [currentPoemDetails?.content, currentPoemDetails?.highlights, showHighlights]);

  // Extract unique poet names using useMemo for better performance
  // This will only include poets that have non-archived poems
  const uniquePoetsFromPoems = useMemo(() => {
    if (poems.length > 0) {
      // Filter out null poet names before sorting
      const poetNames = Array.from(new Set(poems.map(poem => poem.poet_name).filter(Boolean))) as string[];
      return poetNames.sort(); // Sort valid strings
    }
    return [];
  }, [poems]);

  // Update uniquePoets state when uniquePoetsFromPoems changes
  useEffect(() => {
    if (uniquePoetsFromPoems.length > 0) {
      setUniquePoets(uniquePoetsFromPoems);
    }
  }, [uniquePoetsFromPoems]);

  // Extract unique subjects using useMemo for better performance
  const uniqueSubjectsFromPoems = useMemo(() => {
    if (poems.length > 0) {
      // The poems array already excludes archived poems due to the usePoems hook
      const subjects = Array.from(new Set(poems.map(poem => poem.subject).filter(Boolean))) as string[];
      return subjects.sort();
    }
    return [];
  }, [poems]);

  // Update uniqueSubjects state when uniqueSubjectsFromPoems changes
  useEffect(() => {
    if (uniqueSubjectsFromPoems.length > 0) {
      setUniqueSubjects(uniqueSubjectsFromPoems);
      // Initialize currentSubject if not set by URL param later
      if (!currentSubject && !subjectParam) {
        setCurrentSubject(uniqueSubjectsFromPoems[0]);
      }
    }
  }, [uniqueSubjectsFromPoems, currentSubject, subjectParam]); // Added dependencies

  // Dynamically filter poets based on the current subject
  useEffect(() => {
    if (poems.length === 0 || !currentSubject) {
      // If no poems or no subject, use the full unique poets list
      setSubjectFilteredPoets(uniquePoets);
      return;
    }
    
    startTransition(() => {
      const poetsForSubject = Array.from(
        new Set(
          poems
            .filter(poem => poem.subject === currentSubject)
            .map(poem => poem.poet_name)
            .filter(Boolean) // Filter out null/undefined poet names
        )
      ) as string[];
      
      setSubjectFilteredPoets(poetsForSubject.sort());
    });

  }, [poems, currentSubject, uniquePoets, startTransition]); // Depend on poems, currentSubject, and the base uniquePoets

  // Set initialized flag once data is processed
  useEffect(() => {
    if (poems.length > 0 && uniquePoetsFromPoems.length > 0 && uniqueSubjectsFromPoems.length > 0 && !initialized) {
      setInitialized(true);
    }
  }, [poems, uniquePoetsFromPoems, uniqueSubjectsFromPoems, initialized]);

  // Helper to sort poems alphabetically by title, handling potential nulls
  const sortPoemsByTitle = (a: Poem, b: Poem) => {
    const titleA = a.title || '';
    const titleB = b.title || '';
    return titleA.localeCompare(titleB);
  };

  // Update URL when poem changes - only when stable
  useEffect(() => {
    if (!firstLoad && currentPoem && currentPoem.id && currentPoem.poet_name && !isTransitioning && !isPending) { // Added null checks
      const queryParams = new URLSearchParams();
      queryParams.set('poet', currentPoem.poet_name); // Known non-null here
      queryParams.set('poemId', currentPoem.id); // Known non-null here
      if (currentSubject) {
        queryParams.set('subject', currentSubject);
      }
      navigate(`/book?${queryParams.toString()}`, { replace: true });
    }
  }, [currentPoem, navigate, firstLoad, currentSubject, isTransitioning, isPending]);

  // Initialize with the poet, poem, and subject from URL parameters if available
  useEffect(() => {
    if (isPoemsLoading || poems.length === 0 || !initialized) return;
    
    // Prevent multiple initializations
    if (!firstLoad) return;
    
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    // Use startTransition to prioritize UI responsiveness during this heavy computation
    startTransition(() => {
      let poemIndex = 0;
      let targetPoetName = poetParam;
      let targetSubject = subjectParam;
      let initialPoemFound = false;

      // 1. Determine Initial Subject
      // --- MODIFIED START: Prioritize 'Moose' as default subject --- 
      const preferredDefaultSubject = "Moose";
      if (targetSubject && uniqueSubjects.includes(targetSubject)) {
         // Use subject from URL if valid
         setCurrentSubject(targetSubject);
      } else if (uniqueSubjects.includes(preferredDefaultSubject)) {
         // If no valid URL subject, default to 'Moose' if available
         targetSubject = preferredDefaultSubject;
         setCurrentSubject(targetSubject);
         console.log(`Initial subject: Defaulting to preferred subject: ${targetSubject}`);
      } else if (uniqueSubjects.length > 0) {
         // Fallback to first available subject if 'Moose' isn't available
         targetSubject = uniqueSubjects[0]; 
         setCurrentSubject(targetSubject);
         console.log(`Initial subject: Falling back to first available subject (Moose not found): ${targetSubject}`);
      // --- MODIFIED END --- 
      } else {
         // No subjects available at all
         console.error("No subjects found in available poems. Cannot initialize Book page.");
         setFirstLoad(false);
         setIsTransitioning(false);
         toast({ title: "Error", description: "No subjects available to display.", variant: "destructive" });
         return; // Cannot proceed
      }
      
      // 2. Filter poems and poets based on the determined subject
      const poemsForSubject = poems
          .filter(poem => poem.subject === targetSubject)
          .sort(sortPoemsByTitle);
      setPoemsByCurrentSubject(poemsForSubject); // Update state used for poem navigation
      
      const poetsForSubject = Array.from(
          new Set(poemsForSubject.map(poem => poem.poet_name).filter(Boolean))
      ) as string[];
      const sortedPoetsForSubject = poetsForSubject.sort();
      setSubjectFilteredPoets(sortedPoetsForSubject); // Update state used for poet list/navigation
      
      // 3. Determine Initial Poet
      if (targetPoetName && sortedPoetsForSubject.includes(targetPoetName)) {
         setCurrentPoet(targetPoetName);
      } else if (sortedPoetsForSubject.length > 0) {
         // Fallback to first poet for the subject if URL param is invalid or missing
         targetPoetName = sortedPoetsForSubject[0];
         setCurrentPoet(targetPoetName);
         console.log(`Initial poet: Falling back to first available poet for subject ${targetSubject}: ${targetPoetName}`);
      } else {
          // No poets for this subject (shouldn't happen if subject derived from poems)
         console.error(`No poets found for subject ${targetSubject}. Cannot initialize Book page.`);
         setFirstLoad(false);
         setIsTransitioning(false);
         toast({ title: "Error", description: `No poets available for subject ${targetSubject}.`, variant: "destructive" });
         return; // Cannot proceed
      }
      
      // Update poet index
      const initialPoetIndex = sortedPoetsForSubject.findIndex(p => p === targetPoetName);
      setCurrentPoetIndex(initialPoetIndex >= 0 ? initialPoetIndex : 0);
      
      // Update list of all poems by the initial poet (for poet info card etc.)
      const allPoemsByInitialPoet = poems.filter(p => p.poet_name === targetPoetName);
      setPoemsByCurrentPoet(allPoemsByInitialPoet);
      
      // 4. Determine Initial Poem
      if (poemIdParam) {
         // Try to find the specific poem within the subject's poems
         const specificPoemIndexInSubject = poemsForSubject.findIndex(p => p.id === poemIdParam);
         if (specificPoemIndexInSubject >= 0) {
            const specificPoem = poemsForSubject[specificPoemIndexInSubject];
            // Check if this poem also belongs to the targetPoetName (consistency check)
            if (specificPoem.poet_name === targetPoetName) {
               const globalIndex = poems.findIndex(p => p.id === poemIdParam);
               if (globalIndex >= 0) {
                  poemIndex = globalIndex;
                  initialPoemFound = true;
                  setCurrentSubjectPoemIndex(specificPoemIndexInSubject);
                  // Set index within the poet's full list of poems
                  const poetSpecificIndex = allPoemsByInitialPoet.findIndex(p => p.id === poemIdParam);
                  setCurrentPoetPoemIndex(poetSpecificIndex >= 0 ? poetSpecificIndex : 0);
               }
            } else {
               console.warn(`Poem ID ${poemIdParam} found for subject ${targetSubject}, but belongs to poet ${specificPoem.poet_name}, not ${targetPoetName}. Falling back.`);
            }
         } else {
             console.warn(`Poem ID ${poemIdParam} not found for subject ${targetSubject}. Falling back.`);
         }
      }
      
      // Fallback: If specific poem wasn't found/valid, find the first poem by the target poet and subject
      if (!initialPoemFound) {
         const firstPoemForPoetAndSubject = poemsForSubject.find(p => p.poet_name === targetPoetName);
         if (firstPoemForPoetAndSubject) {
            const globalIndex = poems.findIndex(p => p.id === firstPoemForPoetAndSubject.id);
            if (globalIndex >= 0) {
               poemIndex = globalIndex;
               initialPoemFound = true;
               setCurrentSubjectPoemIndex(0); // It's the first poem in the subject list for this poet
               // Set index within the poet's full list of poems
               const poetSpecificIndex = allPoemsByInitialPoet.findIndex(p => p.id === firstPoemForPoetAndSubject.id);
               setCurrentPoetPoemIndex(poetSpecificIndex >= 0 ? poetSpecificIndex : 0);
            }
         }
      }
      
      // Final Fallback: If still no poem found (edge case), use very first poem of the subject
      if (!initialPoemFound && poemsForSubject.length > 0) {
           console.warn(`Could not find a specific poem for poet ${targetPoetName} and subject ${targetSubject}. Using first poem of subject.`);
           const firstSubjectPoem = poemsForSubject[0];
           const globalIndex = poems.findIndex(p => p.id === firstSubjectPoem.id);
            if (globalIndex >= 0) {
               poemIndex = globalIndex;
               initialPoemFound = true;
               setCurrentSubjectPoemIndex(0);
               // Make sure poet states are consistent with this fallback poem
               setCurrentPoet(firstSubjectPoem.poet_name || '');
               const fallbackPoetIndex = sortedPoetsForSubject.findIndex(p => p === firstSubjectPoem.poet_name);
               setCurrentPoetIndex(fallbackPoetIndex >= 0 ? fallbackPoetIndex : 0);
               const fallbackPoetPoems = poems.filter(p => p.poet_name === firstSubjectPoem.poet_name);
               setPoemsByCurrentPoet(fallbackPoetPoems);
               const poetSpecificIndex = fallbackPoetPoems.findIndex(p => p.id === firstSubjectPoem.id);
               setCurrentPoetPoemIndex(poetSpecificIndex >= 0 ? poetSpecificIndex : 0);
            }
      }
      
      if (!initialPoemFound) {
           console.error(`Initialization failed: Could not find any suitable initial poem for subject ${targetSubject} and poet ${targetPoetName}.`);
           setFirstLoad(false);
           setIsTransitioning(false);
           toast({ title: "Error", description: "Could not load the initial poem.", variant: "destructive" });
           return; // Cannot proceed
      }
      
      // Set the final poem state
      setCurrentPoemIndex(poemIndex);
      const selectedPoem = poems[poemIndex];
      setCurrentPoem(selectedPoem);
      setCurrentPoetId(selectedPoem.poet_id); // Fetch poet info for the final selected poem
      
      setFirstLoad(false);
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  }, [poems, poetParam, poemIdParam, subjectParam, isPoemsLoading, uniquePoets, uniqueSubjects, initialized, firstLoad, startTransition, navigate, toast]); // Added uniqueSubjects, navigate, toast

  // Fetch full details when currentPoem ID changes
  useEffect(() => {
    if (currentPoem?.id) {
      const fetchDetails = async () => {
        setIsLoadingPoemDetails(true);
        setPoemDetailsError(null);
        setCurrentPoemDetails(null); // Clear old details
        try {
          const details = await getPoemById(currentPoem.id!);
          setCurrentPoemDetails(details);
          // Log the raw highlights received from the service
          console.log('[Book.tsx] Raw highlights received:', details?.highlights); 
        } catch (error) {
          console.error(`[Book.tsx] Failed to fetch poem details for ID: ${currentPoem.id}`, error);
          const appError = error instanceof AppError ? error : new AppError('Failed to load poem details', ErrorType.UNKNOWN);
          setPoemDetailsError(appError);
          toast({
            title: "Error Loading Poem Details",
            description: appError.message || "Could not load the full poem data.",
            variant: "destructive",
          });
        }
        setIsLoadingPoemDetails(false);
      };
      fetchDetails();
    } else {
      // Clear details if there's no current poem ID
      setCurrentPoemDetails(null);
      setPoemDetailsError(null);
    }
  }, [currentPoem?.id, toast]); // Dependency on currentPoem ID

  // Update current poem based on index (Simplified)
  useEffect(() => {
    if (poems.length === 0 || isPoemsLoading || firstLoad) return;
    
    startTransition(() => {
      const newPoem = poems[currentPoemIndex];
      // We still update the basic currentPoem state for navigation/UI elements
      setCurrentPoem(newPoem);

      if (newPoem) {
        // Update current poet ID for poet info fetching
        console.log(`[Book.tsx Poem Change Effect] Setting currentPoetId to: ${newPoem.poet_id}`);
        setCurrentPoetId(newPoem.poet_id);
        
        // Filter poems by current poet - only if poet changed
        if (newPoem.poet_name !== currentPoet) {
          const poetName = newPoem.poet_name || '';
          setCurrentPoet(poetName);
          
          const filteredPoems = poems.filter(poem => poem.poet_name === poetName);
          setPoemsByCurrentPoet(filteredPoems);
          
          const poemIndex = filteredPoems.findIndex(poem => poem.id === newPoem.id);
          setCurrentPoetPoemIndex(poemIndex >= 0 ? poemIndex : 0);
          
          const poetIndex = uniquePoets.findIndex(poet => poet === poetName);
          setCurrentPoetIndex(poetIndex >= 0 ? poetIndex : 0);
        } else {
          // Just update the current poem index within the poet's poems
          const poemIndex = poemsByCurrentPoet.findIndex(poem => poem.id === newPoem.id);
          if (poemIndex >= 0) {
            setCurrentPoetPoemIndex(poemIndex);
          }
        }
      }
    });
    
    // Reset animation direction after component updates
    setTimeout(() => {
      setAnimationDirection(null);
    }, 300);
    
  }, [currentPoemIndex, poems, uniquePoets, isPoemsLoading, firstLoad, currentPoet, poemsByCurrentPoet, startTransition]); // Keep original dependencies but remove highlight parsing

  // Update navigation boundary state whenever the current poem or filtered poems change
  useEffect(() => {
    if (poemsByCurrentSubject.length <= 1) {
      setIsAtStart(true);
      setIsAtEnd(true);
      return;
    }
    
    setIsAtStart(currentSubjectPoemIndex === 0);
    setIsAtEnd(currentSubjectPoemIndex === poemsByCurrentSubject.length - 1);
  }, [currentSubjectPoemIndex, poemsByCurrentSubject]);

  const selectPoet = (poetName: string) => {
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    startTransition(() => {
      setCurrentPoet(poetName);
      
      // Find index within the *subject-filtered* poet list
      const poetIndex = subjectFilteredPoets.findIndex(poet => poet === poetName);
      if (poetIndex >= 0) {
        setCurrentPoetIndex(poetIndex);
      }
      
      // Find the first poem by this poet AND the current subject
      const firstPoemByPoetAndSubject = poems.find(poem => 
        poem.poet_name === poetName && poem.subject === currentSubject
      );
      
      if (firstPoemByPoetAndSubject) {
        const poemIndex = poems.findIndex(poem => poem.id === firstPoemByPoetAndSubject.id);
        setCurrentPoemIndex(poemIndex);
        setCurrentPoem(firstPoemByPoetAndSubject);
        
        // Update current poet ID for poet info fetching
        setCurrentPoetId(firstPoemByPoetAndSubject.poet_id);
        
        // Update poemsByCurrentPoet (list of poems by this poet, regardless of subject)
        const allPoetPoems = poems.filter(poem => poem.poet_name === poetName);
        setPoemsByCurrentPoet(allPoetPoems);
        
        // Find index within the subject-specific list for poem navigation
        const subjectIndex = poemsByCurrentSubject.findIndex(p => p.id === firstPoemByPoetAndSubject.id);
        setCurrentSubjectPoemIndex(subjectIndex >= 0 ? subjectIndex : 0);
        
        // Find index within the poet-specific list 
        const poetSpecificIndex = allPoetPoems.findIndex(p => p.id === firstPoemByPoetAndSubject.id);
        setCurrentPoetPoemIndex(poetSpecificIndex >= 0 ? poetSpecificIndex : 0);
      } else {
        // This case should ideally not be reachable if the poet list is correctly filtered by subject
        console.warn(`No poems found for poet \"${poetName}\" and subject \"${currentSubject}\". This might indicate an inconsistency.`);
        // Fallback? Maybe select the first poem of the subject?
        if (poemsByCurrentSubject.length > 0) {
           const firstSubjectPoemId = poemsByCurrentSubject[0].id;
           const globalIndex = poems.findIndex(p => p.id === firstSubjectPoemId);
           if (globalIndex !== -1) {
              setCurrentPoemIndex(globalIndex);
              setCurrentPoem(poems[globalIndex]);
           }
        }
      }
      
      setShowPoetList(false);
      setAnimationDirection('up'); // Or null if no animation needed
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  // Add functions for subject navigation
  const nextSubject = () => {
    if (uniqueSubjects.length <= 1) return;
    
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    startTransition(() => {
      const newIndex = (uniqueSubjects.indexOf(currentSubject) + 1) % uniqueSubjects.length;
      const nextSubject = uniqueSubjects[newIndex];
      
      selectSubject(nextSubject);
      // Use page-settle animation for vertical navigation
      setAnimationDirection('up');
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };
  
  const prevSubject = () => {
    if (uniqueSubjects.length <= 1) return;
    
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    startTransition(() => {
      const currentIndex = uniqueSubjects.indexOf(currentSubject);
      const newIndex = (currentIndex - 1 + uniqueSubjects.length) % uniqueSubjects.length;
      const prevSubject = uniqueSubjects[newIndex];
      
      selectSubject(prevSubject);
      // Use page-settle animation for vertical navigation
      setAnimationDirection('down');
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };
  
  const selectSubject = (subject: string) => {
    startTransition(() => {
      setCurrentSubject(subject);
      
      // Filter poems by this subject and sort alphabetically by title
      const subjectPoems = poems
        .filter(poem => poem.subject === subject)
        .sort(sortPoemsByTitle);
      setPoemsByCurrentSubject(subjectPoems);
      
      // Determine the poets who have poems for this subject
      const poetsForNewSubject = Array.from(
        new Set(
          subjectPoems
            .map(poem => poem.poet_name)
            .filter(Boolean) // Filter out null/undefined poet names
        )
      ) as string[];
      const sortedPoetsForNewSubject = poetsForNewSubject.sort();
      setSubjectFilteredPoets(sortedPoetsForNewSubject); // Update the subject-filtered poet list
      
      if (subjectPoems.length > 0) {
        // Select the first poet from the newly filtered list
        const firstPoetName = sortedPoetsForNewSubject[0];
        setCurrentPoet(firstPoetName);
        setCurrentPoetIndex(0);
        
        // Find the first poem by the selected subject AND the first poet
        const firstPoem = subjectPoems.find(p => p.poet_name === firstPoetName);
        
        if (firstPoem) {
            const poemIndex = poems.findIndex(poem => poem.id === firstPoem.id);
            setCurrentPoemIndex(poemIndex);
            setCurrentPoem(poems[poemIndex]);
            setCurrentSubjectPoemIndex(0); // Reset poem index within subject list
            
            // Update current poet ID for poet info fetching
            setCurrentPoetId(firstPoem.poet_id);
            
            // Update poemsByCurrentPoet (list of poems by this poet, regardless of subject)
            const allPoetPoems = poems.filter(poem => poem.poet_name === firstPoetName);
            setPoemsByCurrentPoet(allPoetPoems);
            
             // Find index within the poet-specific list
            const poetSpecificIndex = allPoetPoems.findIndex(p => p.id === firstPoem.id);
            setCurrentPoetPoemIndex(poetSpecificIndex >= 0 ? poetSpecificIndex : 0);
        } else {
             // Should not happen if poet list derived correctly
             console.warn(`Inconsistency: Could not find first poem for subject \"${subject}\" and poet \"${firstPoetName}\".`);
             // Fallback: Select first poem of subject
             const globalIndexFallback = poems.findIndex(p => p.id === subjectPoems[0].id);
             if (globalIndexFallback !== -1) {
                 setCurrentPoemIndex(globalIndexFallback);
                 setCurrentPoem(poems[globalIndexFallback]);
                 setCurrentPoet(poems[globalIndexFallback].poet_name || '');
                 setCurrentPoetId(poems[globalIndexFallback].poet_id);
             }
        }
      } else {
        // No poems for this subject (shouldn't happen if subject list is derived from available poems)
        console.warn("No non-archived poems found for subject:", subject);
        setCurrentPoem(null);
        setCurrentPoet('');
        setCurrentPoetId(null);
        setPoemsByCurrentSubject([]);
        setSubjectFilteredPoets([]);
        setCurrentPoemIndex(-1); // Indicate no poem selected
        setCurrentPoetIndex(-1);
        setCurrentSubjectPoemIndex(-1);
        setCurrentPoetPoemIndex(-1);
        toast({
          title: "No poems available",
          description: `No available poems found for the subject "${subject}".`,
          variant: "default"
        });
      }
      
      setShowSubjectList(false);
    });
  };

  // Optimize the poem navigation functions to prevent flickering and handle filtered poems
  const nextPoem = () => {
    if (poemsByCurrentSubject.length <= 1) return;
    
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    startTransition(() => {
      const nextIndex = (currentSubjectPoemIndex + 1) % poemsByCurrentSubject.length;
      const nextPoemId = poemsByCurrentSubject[nextIndex].id;
      const globalIndex = poems.findIndex(poem => poem.id === nextPoemId);
      
      if (globalIndex >= 0) {
        setAnimationDirection('left');
        setCurrentPoemIndex(globalIndex);
        setCurrentPoem(poems[globalIndex]);
        setCurrentSubjectPoemIndex(nextIndex);
        
        // Update poet-related state for consistency
        if (poems[globalIndex]) {
          const poetName = poems[globalIndex].poet_name || '';
          setCurrentPoet(poetName);
          setCurrentPoetId(poems[globalIndex].poet_id);
          
          // Update poet poems
          const poetPoems = poems.filter(poem => poem.poet_name === poetName);
          setPoemsByCurrentPoet(poetPoems);
          const poetPoemIndex = poetPoems.findIndex(poem => poem.id === nextPoemId);
          setCurrentPoetPoemIndex(poetPoemIndex >= 0 ? poetPoemIndex : 0);
        }
      }
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  const prevPoem = () => {
    if (poemsByCurrentSubject.length <= 1) return;
    
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    startTransition(() => {
      const prevIndex = (currentSubjectPoemIndex - 1 + poemsByCurrentSubject.length) % poemsByCurrentSubject.length;
      const prevPoemId = poemsByCurrentSubject[prevIndex].id;
      const globalIndex = poems.findIndex(poem => poem.id === prevPoemId);
      
      if (globalIndex >= 0) {
        setAnimationDirection('right');
        setCurrentPoemIndex(globalIndex);
        setCurrentPoem(poems[globalIndex]);
        setCurrentSubjectPoemIndex(prevIndex);
        
        // Update poet-related state for consistency
        if (poems[globalIndex]) {
          const poetName = poems[globalIndex].poet_name || '';
          setCurrentPoet(poetName);
          setCurrentPoetId(poems[globalIndex].poet_id);
          
          // Update poet poems
          const poetPoems = poems.filter(poem => poem.poet_name === poetName);
          setPoemsByCurrentPoet(poetPoems);
          const poetPoemIndex = poetPoems.findIndex(poem => poem.id === prevPoemId);
          setCurrentPoetPoemIndex(poetPoemIndex >= 0 ? poetPoemIndex : 0);
        }
      }
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  // Update the nextPoet and prevPoet functions to handle filtered poets
  const nextPoet = () => {
    // Navigate through the subject-filtered list
    if (subjectFilteredPoets.length <= 1) return;
    
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    startTransition(() => {
      const newIndex = (currentPoetIndex + 1) % subjectFilteredPoets.length;
      const poetName = subjectFilteredPoets[newIndex];
      
      setCurrentPoetIndex(newIndex);
      setCurrentPoet(poetName);
      
      // Find poems by this poet AND the current subject
      const poetSubjectPoems = poems.filter(poem => 
        poem.poet_name === poetName && poem.subject === currentSubject
      ).sort(sortPoemsByTitle);
      
      if (poetSubjectPoems.length > 0) {
        // Get the first poem from this filtered list
        const firstPoemIndex = poems.findIndex(poem => poem.id === poetSubjectPoems[0].id);
        if (firstPoemIndex >= 0) {
          setAnimationDirection('up');
          setCurrentPoemIndex(firstPoemIndex);
          setCurrentPoem(poems[firstPoemIndex]);
          
          // Update current poet ID for poet info fetching
          setCurrentPoetId(poems[firstPoemIndex].poet_id);
          
          // Update poemsByCurrentPoet (the list of poems by this poet, regardless of subject for other potential uses)
          const allPoetPoems = poems.filter(poem => poem.poet_name === poetName);
          setPoemsByCurrentPoet(allPoetPoems);
          
          // Find index within the subject-specific list for poem navigation
          const subjectIndex = poemsByCurrentSubject.findIndex(p => p.id === poetSubjectPoems[0].id);
          setCurrentSubjectPoemIndex(subjectIndex >= 0 ? subjectIndex : 0);
          
          // Find index within the poet-specific list 
          const poetIndex = allPoetPoems.findIndex(p => p.id === poetSubjectPoems[0].id);
          setCurrentPoetPoemIndex(poetIndex >= 0 ? poetIndex : 0);
        }
      } else {
        console.warn("No non-archived poems found for poet:", poetName, "and subject:", currentSubject);
      }
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  const prevPoet = () => {
    // Navigate through the subject-filtered list
    if (subjectFilteredPoets.length <= 1) return;
    
    // Start transition to prevent flickering
    setIsTransitioning(true);
    
    startTransition(() => {
      const prevPoetIndex = (currentPoetIndex - 1 + subjectFilteredPoets.length) % subjectFilteredPoets.length;
      const prevPoetName = subjectFilteredPoets[prevPoetIndex];
      
      setCurrentPoetIndex(prevPoetIndex);
      setCurrentPoet(prevPoetName);
      
      // Find poems by this poet AND the current subject
      const poetSubjectPoems = poems.filter(poem => 
        poem.poet_name === prevPoetName && poem.subject === currentSubject
      ).sort(sortPoemsByTitle);
      
      if (poetSubjectPoems.length > 0) {
        // Get the first poem from this filtered list
        const firstPoemIndex = poems.findIndex(poem => poem.id === poetSubjectPoems[0].id);
        if (firstPoemIndex >= 0) {
          setAnimationDirection('down');
          setCurrentPoemIndex(firstPoemIndex);
          setCurrentPoem(poems[firstPoemIndex]);
          
          // Update current poet ID for poet info fetching
          setCurrentPoetId(poems[firstPoemIndex].poet_id);
          
          // Update poemsByCurrentPoet (list of poems by this poet, regardless of subject)
          const allPoetPoems = poems.filter(poem => poem.poet_name === prevPoetName);
          setPoemsByCurrentPoet(allPoetPoems);
          
          // Find index within the subject-specific list for poem navigation
          const subjectIndex = poemsByCurrentSubject.findIndex(p => p.id === poetSubjectPoems[0].id);
          setCurrentSubjectPoemIndex(subjectIndex >= 0 ? subjectIndex : 0);
          
           // Find index within the poet-specific list
          const poetIndex = allPoetPoems.findIndex(p => p.id === poetSubjectPoems[0].id);
          setCurrentPoetPoemIndex(poetIndex >= 0 ? poetIndex : 0);
        }
      } else {
        console.warn("No non-archived poems found for poet:", prevPoetName, "and subject:", currentSubject);
      }
    });
    
    // End transition after state updates are committed
    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  // Add more detailed debug logging for style points
  useEffect(() => {
    if (currentPoetInfo?.data) {
      console.log("Current poet:", currentPoetInfo.data.name);
      console.log("Loading state:", isPoetLoading);
      console.log("Style points:", currentPoetInfo.data.style_points);
      
      // Check the type of style_points to help with debugging
      if (currentPoetInfo.data.style_points) {
        console.log("Style points type:", typeof currentPoetInfo.data.style_points);
        console.log("Is array:", Array.isArray(currentPoetInfo.data.style_points));
        
        if (typeof currentPoetInfo.data.style_points === 'object' && !Array.isArray(currentPoetInfo.data.style_points)) {
          console.log("Style points keys:", Object.keys(currentPoetInfo.data.style_points || {}));
        }
      } else {
        console.log("No style points available for this poet");
      }
    }
  }, [currentPoetInfo]);

  // Filter poets based on search query
  useEffect(() => {
    if (poetSearchQuery.trim() === '') {
      // Use the subject-filtered list when query is empty
      setFilteredPoets(subjectFilteredPoets);
    } else {
      const query = poetSearchQuery.toLowerCase().trim();
      // Filter from the subject-filtered list
      const filtered = subjectFilteredPoets.filter(poet =>
        poet.toLowerCase().includes(query)
      );
      setFilteredPoets(filtered);
    }
  }, [poetSearchQuery, subjectFilteredPoets]); // Depend on subjectFilteredPoets now
  
  // Handle click outside to dismiss lists
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close poet list if click is outside
      if (showPoetList && 
          poetListRef.current && 
          !poetListRef.current.contains(event.target as Node) &&
          poetListButtonRef.current && 
          !poetListButtonRef.current.contains(event.target as Node)) {
        setShowPoetList(false);
      }
      
      // Close subject list if click is outside
      if (showSubjectList && 
          subjectListRef.current && 
          !subjectListRef.current.contains(event.target as Node) &&
          subjectListButtonRef.current && 
          !subjectListButtonRef.current.contains(event.target as Node)) {
        setShowSubjectList(false);
      }
    };
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPoetList, showSubjectList]);

  // Add a special handler for direct navigation to poems by ID
  useEffect(() => {
    const loadPoemById = async () => {
      if (poemIdParam && !isPoemsLoading && initialized) {
        try {
          const foundPoem = poems.find(poem => poem.id === poemIdParam);
          if (foundPoem && foundPoem.content) {
            setCurrentPoem(foundPoem);
            setCurrentPoemIndex(poems.findIndex(p => p.id === poemIdParam));
            
            // Also update poet/subject context if needed
            const poetName = foundPoem.poet_name;
            if (poetName && poetName !== currentPoet && uniquePoets.includes(poetName)) {
              setCurrentPoet(poetName);
              setCurrentPoetIndex(uniquePoets.indexOf(poetName));
              const poetPoems = poems.filter(p => p.poet_name === poetName).sort(sortPoemsByTitle);
              setPoemsByCurrentPoet(poetPoems);
              setCurrentPoetPoemIndex(poetPoems.findIndex(p => p.id === poemIdParam));
            }
            const subjectName = foundPoem.subject;
            if (subjectName && subjectName !== currentSubject) {
              setCurrentSubject(subjectName);
              const subjectPoems = poems.filter(p => p.subject === subjectName).sort(sortPoemsByTitle);
              setPoemsByCurrentSubject(subjectPoems);
              setCurrentSubjectPoemIndex(subjectPoems.findIndex(p => p.id === poemIdParam));
            }
            
            return;
          }
          
          // If the poem wasn't in our filtered list, it might be archived
          // Show a notification and recover by finding another poem
          toast({
            title: "Poem not available",
            description: "The requested poem is no longer available. Showing other available poems instead.",
            variant: "destructive"
          });
          
          // Recovery strategy: Try to show another poem by the same poet if possible
          if (poetParam && poems.length > 0) {
            const poetPoems = poems.filter(poem => poem.poet_name === poetParam);
            
            if (poetPoems.length > 0) {
              // Navigate to the first available poem by this poet
              const newPoemIndex = poems.findIndex(poem => poem.id === poetPoems[0].id);
              
              if (newPoemIndex >= 0) {
                setCurrentPoemIndex(newPoemIndex);
                setCurrentPoem(poems[newPoemIndex]);
                
                // Create the new URL
                const queryParams = new URLSearchParams();
                queryParams.set('poet', poetParam);
                // Add checks for poetPoems and id
                if (poetPoems.length > 0) {
                  const firstPoemId = poetPoems[0]?.id;
                  if (firstPoemId) {
                    queryParams.set('poemId', firstPoemId);
                  }
                }
                if (subjectParam) {
                  queryParams.set('subject', subjectParam);
                }
                
                // Replace the current URL to avoid back button issues
                navigate(`/book?${queryParams.toString()}`, { replace: true });
                return;
              }
            }
          }
          
          // If we couldn't recover with a specific poet, show the first non-archived poem
          if (poems.length > 0) {
            setCurrentPoemIndex(0);
            setCurrentPoem(poems[0]);
            
            const poetName = poems[0].poet_name || '';
            setCurrentPoet(poetName);
            setCurrentPoetId(poems[0].poet_id);
            
            // Update URL
            const queryParams = new URLSearchParams();
            if (poetName) queryParams.set('poet', poetName);
            const firstPoemId = poems[0]?.id; // Safely access id
            if (firstPoemId) { // Check if id exists
              queryParams.set('poemId', firstPoemId);
            }
            
            navigate(`/book?${queryParams.toString()}`, { replace: true });
          }
        } catch (error) {
          console.error("Error handling direct poem navigation:", error);
          toast({
            title: "Error",
            description: "An error occurred while loading the poem",
            variant: "destructive"
          });
        }
      }
    };
    
    loadPoemById();
  }, [poemIdParam, poems, isPoemsLoading, initialized, toast, navigate, poetParam, subjectParam, uniquePoets]);

  // Show loading state while fetching initial poems OR poem details
  if (isPoemsLoading || !initialized || isLoadingPoemDetails) {
    // Return a skeleton UI that maintains the layout to prevent flickering
    return (
      <div className="min-h-screen pt-16" data-testid="book-component">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-wrap justify-between items-center mb-8">
            <h1 className="font-poetry text-4xl font-semibold flex items-center flex-wrap">
              A Big Book of Poems About{" "}
              <span className="relative inline-flex items-center group ml-2">
                <div className="h-6 w-32 bg-gray-200 animate-pulse rounded"></div>
              </span>
            </h1>
            <div className="h-10 w-32 bg-gray-200 animate-pulse rounded"></div>
          </div>
          
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="book-shadow page-turn rounded-lg overflow-hidden bg-card border">
                <div className="flex relative">
                  <div className="w-5/6 p-8 bg-background min-h-[400px] ml-[16.666667%]">
                    <Card className="border-0 shadow-none bg-background fade-scale h-full">
                      <CardContent className="p-6 h-full flex flex-col">
                        <div className="max-w-2xl mx-auto w-full mb-8">
                          <div className="h-8 w-64 mx-auto bg-gray-200 animate-pulse rounded mb-3"></div>
                          <div className="flex justify-center items-center mb-6">
                            <div className="h-6 w-40 bg-gray-200 animate-pulse rounded"></div>
                          </div>
                          <div className="h-64 bg-gray-100 animate-pulse rounded"></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="w-1/6 bg-muted p-4 border-r absolute inset-y-0 left-0"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error fetching initial poems OR poem details
  const actualError = poemsError || poemDetailsError;
  if (actualError && !isLoadingPoemDetails && !isPoemsLoading && initialized) {
    return (
      <ErrorState 
        error={actualError}
        onRetry={poemsError ? refetchPoems : () => { /* Add retry logic for details fetch if needed */ }}
        title={poemsError ? "Failed to load poems" : "Failed to load poem details"}
      />
    );
  }

  // Show message if no poems are found OR if current poem details failed to load for some reason
  if (poems.length === 0 || !currentPoemDetails) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h1 className="text-2xl font-bold mb-4">No Poem Data</h1>
        <p className="text-muted-foreground mb-6">
          {poems.length === 0 ? "There are no poems in the database." : "Could not load the selected poem's details."}
        </p>
        <Button asChild>
          <Link to="/">Return Home</Link>
        </Button>
      </div>
    );
  }

  // No current poem state (shouldn't happen, but just in case)
  if (!currentPoem) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <h2 className="text-2xl font-poetry mb-4">Poem Not Found</h2>
          <p className="mb-6">The requested poem could not be found.</p>
          <Link to="/book">
            <Button>View All Poems</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isLongPoem = (currentPoem?.content ?? '').split('\n').length > 10;

  const styles = `
    @keyframes subject-change-up {
      0% {
        transform: translateY(10px);
        opacity: 0;
      }
      100% {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes subject-change-down {
      0% {
        transform: translateY(-10px);
        opacity: 0;
      }
      100% {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .subject-change-up {
      animation: subject-change-up 0.3s ease-out forwards;
    }

    .subject-change-down {
      animation: subject-change-down 0.3s ease-out forwards;
    }
    
    .poet-change-up {
      animation: subject-change-up 0.3s ease-out forwards;
    }

    .poet-change-down {
      animation: subject-change-down 0.3s ease-out forwards;
    }
    
    /* Scrollbar styling */
    .scrollbar-style::-webkit-scrollbar {
      width: 8px;
    }
    
    .scrollbar-style::-webkit-scrollbar-track {
      background: rgba(139, 92, 246, 0.25);
    }
    
    .scrollbar-style::-webkit-scrollbar-thumb {
      background-color: rgba(139, 92, 246, 0.3);
      border-radius: 6px;
      height: 22px;
    }
    
    /* Add transition styles to prevent flickering */
    .content-transition {
      transition: opacity 0.15s ease-in-out;
    }
    
    .content-transition.transitioning {
      opacity: 0.7;
    }
  `;

  return (
    <div className="min-h-screen pt-16" data-testid="book-component">
      <style>{`
        ${styles}
        
        /* Enhanced scrollbar styling with fixed height */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(139, 92, 246, 0.5) rgba(139, 92, 246, 0.2);
          overflow-y: overlay;
        }
        
        /* Remove problematic rule - completely deleting it rather than commenting it out */
        
        /* Rest of the style rules... */
      `}</style>
      
      {/* Add indicator for pending UI update to keep banner responsive */}
      <div aria-hidden={true} className={`fixed top-0 left-0 w-full h-1 z-50 ${isPending ? 'bg-indigo-500 animate-pulse' : 'bg-transparent'}`}></div>
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex flex-wrap justify-between items-center">
          <h1 className="font-poetry text-4xl font-semibold flex items-center flex-wrap">
            A Big Book of Poems About{" "}
            <span className="relative inline-flex items-center group ml-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={prevSubject}
                className="absolute -top-5 left-1/2 transform -translate-x-1/2 h-6 w-6 opacity-60 hover:opacity-100 transition-opacity !bg-transparent"
                aria-label="Previous subject"
                disabled={uniqueSubjects.length <= 1}
                data-testid="prev-subject-button"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <span 
                className={`font-semibold text-[#403E43] ${animationDirection ? (animationDirection === 'up' ? 'subject-change-up' : 'subject-change-down') : ''}`}
                data-testid="subject-name"
              >
                {currentSubject}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={nextSubject}
                className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 h-6 w-6 opacity-60 hover:opacity-100 transition-opacity !bg-transparent"
                aria-label="Next subject"
                disabled={uniqueSubjects.length <= 1}
                data-testid="next-subject-button"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </span>
            <div className="relative inline-block ml-2">
              <Button
                variant="ghost"
                size="icon"
                ref={subjectListButtonRef}
                onClick={() => setShowSubjectList(!showSubjectList)}
                className="h-8 w-8 opacity-80 hover:opacity-100 transition-opacity hover:bg-gray-100"
                aria-label="Show subject list"
              >
                <List className="h-4 w-4" />
              </Button>
              {/* Improved subject dropdown list with better positioning */}
              {showSubjectList && (
                <div 
                  ref={subjectListRef}
                  className="absolute z-50 bg-white rounded-md shadow-lg border border-gray-200 p-2 max-h-80 overflow-y-auto custom-scrollbar w-64"
                  style={{
                    top: "calc(100% + 5px)",
                    left: "50%",
                    transform: "translateX(-50%)"
                  }}
                >
                  <div className="flex justify-between items-center mb-2 pb-2 border-b">
                    <h3 className="font-medium text-sm">Select a Subject</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowSubjectList(false)}
                      className="h-6 w-6 hover:bg-gray-100 rounded-full"
                      aria-label="Close subject list"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {uniqueSubjects.sort().map((subject) => (
                    <button
                      key={subject}
                      onClick={() => selectSubject(subject)}
                      className={`block w-full text-left px-4 py-2 text-sm rounded-md ${
                        subject === currentSubject 
                          ? 'bg-[#8B5CF6]/10 text-[#8B5CF6] font-medium' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </h1>
          
          {/* Add highlight toggle switch */}
          <div className="flex items-center space-x-2 mt-2 md:mt-0">
            <Switch 
              checked={showHighlights}
              onCheckedChange={setShowHighlights}
              id="highlight-toggle"
            />
            <Label htmlFor="highlight-toggle" className="text-sm">
              Highlights
            </Label>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12 content-transition ${isTransitioning ? 'transitioning' : ''}">
        <div className="flex justify-center">
          <div className="w-full max-w-4xl">
            <div className="book-shadow page-turn rounded-lg overflow-hidden bg-card border">
              <div className="flex relative">
                <div className="w-1/6 bg-muted p-4 border-r absolute inset-y-0 left-0">
                  {/* View All Poets button - repositioned to align with poem title */}
                  <div className="absolute left-0 right-0 top-[4.5rem] flex justify-center">
                    <Link to="/poets">
                      <Button variant="outline" size="sm" className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span className="text-xs">View Poets</span>
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="absolute left-0 right-0 top-[150px] flex flex-col items-center space-y-4 z-10">
                    <div className="group relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={prevPoem}
                        className={`nav-button-horizontal opacity-70 hover:opacity-100 transition-opacity !bg-transparent relative z-20 nav-button-enhanced ${firstLoad ? 'nav-button-pulse' : ''}`}
                        aria-label="Previous poem by same subject"
                        disabled={poemsByCurrentSubject.length <= 1}
                        data-testid="prev-poem-button"
                      >
                        <ChevronLeft className="h-8 w-8" />
                      </Button>
                      <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity left-1/2 top-1/2 -translate-x-[calc(100%+8px)] -translate-y-1/2">
                        <div className="h-[12px] w-[1px] bg-current opacity-60"></div>
                      </div>
                    </div>
                    
                    <div className="text-sm text-center text-muted-foreground">
                      {poemsByCurrentSubject.length > 1 ? (
                        <span>{currentSubjectPoemIndex + 1} / {poemsByCurrentSubject.length}</span>
                      ) : (
                        <span>1 / 1</span>
                      )}
                    </div>
                    
                    <div className="group relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={nextPoem}
                        className={`nav-button-horizontal opacity-70 hover:opacity-100 transition-opacity !bg-transparent relative z-20 nav-button-enhanced ${firstLoad ? 'nav-button-pulse' : ''}`}
                        aria-label="Next poem by same subject"
                        disabled={poemsByCurrentSubject.length <= 1}
                        data-testid="next-poem-button"
                      >
                        <ChevronRight className="h-8 w-8" />
                      </Button>
                      <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity left-1/2 top-1/2 translate-x-[calc(100%+8px)] -translate-y-1/2">
                        <div className="h-[12px] w-[1px] bg-current opacity-60"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-5/6 p-8 bg-background min-h-[400px] ml-[16.666667%]">
                  <Card className="border-0 shadow-none bg-background fade-scale h-full">
                    <CardContent className="p-6 h-full flex flex-col">
                      <div className="max-w-2xl mx-auto w-full mb-8">
                        <h2 
                          className={`font-poetry text-3xl font-semibold mb-3 text-[#403E43] text-center ${animationDirection ? 'page-flip' : ''}`} 
                          data-testid="poem-title"
                        >
                          {currentPoem.title}
                        </h2>
                        
                        <div className="flex justify-center items-center mb-6">
                          <div className="text-sm text-muted-foreground italic text-center">
                            <em>Inspired by the style of</em>{" "}
                            <div 
                              className="relative inline-flex items-center group ml-[4px]"
                              ref={poetNameRef}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={prevPoet}
                                className="absolute -top-4 left-1/2 transform -translate-x-1/2 h-6 w-6 opacity-60 hover:opacity-100 transition-opacity !bg-transparent"
                                aria-label="Previous poet"
                                disabled={uniquePoets.length <= 1}
                                data-testid="prev-poet-button"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                              <span 
                                className={`font-semibold text-[#403E43] ${animationDirection ? (animationDirection === 'up' ? 'poet-change-up' : 'poet-change-down') : ''}`} 
                                data-testid="poet-name"
                              >
                                {currentPoem.poet_name}
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={nextPoet}
                                className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 h-6 w-6 opacity-60 hover:opacity-100 transition-opacity !bg-transparent"
                                aria-label="Next poet"
                                disabled={uniquePoets.length <= 1}
                                data-testid="next-poet-button"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Poet list button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            ref={poetListButtonRef}
                            onClick={() => setShowPoetList(!showPoetList)}
                            className="ml-2 h-8 w-8 opacity-80 hover:opacity-100 transition-opacity hover:bg-gray-100"
                            aria-label="Show poet list"
                          >
                            <List className="h-4 w-4" />
                          </Button>
                          
                          {/* Improved filterable poet list */}
                          {showPoetList && (
                            <div 
                              ref={poetListRef}
                              className="absolute z-50 mt-2 bg-white rounded-md shadow-lg border border-gray-200 p-2 max-h-80 custom-scrollbar right-4 top-20 w-72"
                            >
                              <div className="flex justify-between items-center mb-2 pb-2 border-b">
                                <h3 className="font-medium text-sm">Select a Poet</h3>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setShowPoetList(false)}
                                  className="h-6 w-6 hover:bg-gray-100 rounded-full"
                                  aria-label="Close poet list"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="mb-2 relative">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Search poets..."
                                  value={poetSearchQuery}
                                  onChange={(e) => setPoetSearchQuery(e.target.value)}
                                  className="w-full pl-8 pr-4 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/50"
                                />
                                {poetSearchQuery && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setPoetSearchQuery('')}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-5 w-5 hover:bg-gray-100 rounded-full"
                                    aria-label="Clear search"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                {filteredPoets.length > 0 ? (
                                  filteredPoets.sort().map((poet) => (
                                    <button
                                      key={poet}
                                      onClick={() => {
                                        selectPoet(poet);
                                        setPoetSearchQuery('');
                                      }}
                                      className={`block w-full text-left px-4 py-2 text-sm rounded-md mb-1 ${
                                        poet === currentPoet 
                                          ? 'bg-[#8B5CF6]/10 text-[#8B5CF6] font-medium' 
                                          : 'hover:bg-gray-100'
                                      }`}
                                    >
                                      {poet}
                                    </button>
                                  ))
                                ) : (
                                  <p className="text-center py-4 text-gray-500 text-sm">
                                    No poets found matching "{poetSearchQuery}"
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Poem content with always visible scrollbar */}
                        <div className="flex flex-col gap-4 relative">
                          <div className="poem-content-container relative w-full flex flex-col">
                            {/* Poem content with transitions */}
                            <div
                              className={`poem-content max-w-2xl mx-auto w-full p-6 ${
                                isTransitioning ? `transition-out-${animationDirection}` : ''
                              } ${firstLoad ? '' : `transition-in-${animationDirection}`}`}
                              ref={poemContentRef}
                            >
                              <HighlightTooltipProvider>
                                {currentPoemDetails && currentPoemDetails.content && (
                                  <>
                                    {/* Poem lines rendered directly using memoized data */} 
                                    <div className="space-y-4 font-poetry text-lg leading-relaxed">
                                      {(typeof currentPoemDetails.content === 'string' 
                                          ? currentPoemDetails.content.split('\n') 
                                          : []
                                       ).map((line: string, index: number) => {
                                        const lineHighlights = processedData?.highlightsByLine?.[index] || [];
                                        return (
                                          <HighlightedLine
                                            key={index}
                                            line={line}
                                            lineIndex={index}
                                            lineHighlights={lineHighlights} // Correct prop name
                                            showHighlights={showHighlights}
                                            activeHighlightId={activeHighlight || undefined} // Correct prop name
                                            onHighlightInteraction={setActiveHighlight}
                                            data-testid={`line-${index}`}
                                          />
                                        );
                                      })}
                                    </div>

                                    {/* Highlight legend section using memoized data */} 
                                    {showHighlights && currentPoemDetails.highlights && currentPoemDetails.highlights.length > 0 && processedData && (
                                      <div className="mt-4">
                                        <HighlightLegend
                                          legendItems={processedData.legendItems || []}
                                          showHighlights={showHighlights}
                                          activeHighlightId={activeHighlight || undefined}
                                          onHighlightInteraction={setActiveHighlight}
                                          data-testid="highlight-legend"
                                        />
                                      </div>
                                    )}
                                  </>
                                )}
                              </HighlightTooltipProvider>
                            </div>
                          </div>
                        </div>
                        
                        {/* Poet information section */}
                        {currentPoetInfo ? (
                          <div className="mt-6 pt-4">
                            <Separator className="mb-4" />
                            <div className="flex items-start space-x-2">
                              <BookUser className="h-5 w-5 text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                              <div>
                                <h3 className="text-lg font-medium text-[#403E43] mb-2">About {currentPoem.poet_name}'s Style</h3>
                                {/* Style Points Section */}
                                <div>
                                  {currentPoetInfo.data ? (
                                    <>
                                      {currentPoetInfo.data.style_points ? (
                                        Array.isArray(currentPoetInfo.data.style_points) ? (
                                          // Handle array of strings format
                                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground ml-1 mb-3">
                                            {(currentPoetInfo.data.style_points as string[]).map((point, index) => (
                                              <li key={index}>{point}</li>
                                            ))}
                                          </ul>
                                        ) : typeof currentPoetInfo.data.style_points === 'object' ? (
                                          // Handle object format (key-value pairs)
                                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground ml-1 mb-3">
                                            {Object.entries(currentPoetInfo.data.style_points as Record<string, Json>).map(([key, value], index) => (
                                              <li key={index}>
                                                <strong>{key}:</strong> {typeof value === 'number' ? value.toFixed(1) : String(value || '')}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          // Fallback for other formats
                                          <p className="text-sm text-muted-foreground ml-1 mb-3">Style information is available in a different format.</p>
                                        )
                                      ) : (
                                        <p className="text-sm text-muted-foreground ml-1 mb-3">No style points available for this poet.</p>
                                      )}
                                    </>
                                  ) : (
                                    <div className="animate-pulse">
                                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Poetry Foundation Link */}
                                <div className="mt-4">
                                  {currentPoetInfo?.data?.poetry_foundation_url ? (
                                    <Button variant="outline" className="w-full" asChild>
                                      <a
                                        href={currentPoetInfo.data.poetry_foundation_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center"
                                        data-testid="poet-link"
                                      >
                                        <BookUser className="mr-2 h-4 w-4" />
                                        Learn More
                                        <ExternalLink className="ml-2 h-3 w-3" />
                                      </a>
                                    </Button>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">No external resources available</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Show skeleton UI for poet information while loading
                          <div className="mt-6 pt-4">
                            <Separator className="mb-4" />
                            <div className="flex items-start space-x-2">
                              <div className="h-5 w-5 bg-gray-200 animate-pulse rounded mt-0.5 flex-shrink-0"></div>
                              <div className="w-full">
                                <div className="h-6 bg-gray-200 animate-pulse rounded w-48 mb-4"></div>
                                <div className="animate-pulse space-y-2 mb-4">
                                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                </div>
                                <div className="h-9 bg-gray-200 animate-pulse rounded w-full mt-4"></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Error message for tests */}
      {false && <div data-testid="error-message">Network error occurred</div>}
    </div>
  );
};

export default Book;
